import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Tag, Plus, Edit, Trash2, X, Save, Percent, DollarSign, 
  Calendar, Users, Check, AlertCircle
} from 'lucide-react';

export default function ManageCoupons() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_purchase: 0,
    max_discount: null,
    valid_until: '',
    usage_limit: null,
    user_limit: 1,
    applicable_to: [],
    is_active: true
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Failed to load coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        max_discount: formData.max_discount || null,
        usage_limit: formData.usage_limit || null
      };

      if (editingId) {
        await axios.put(`${API}/admin/coupons/${editingId}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/coupons`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      resetForm();
      loadCoupons();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleEdit = (coupon) => {
    setFormData({
      code: coupon.code,
      description: coupon.description,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_purchase: coupon.min_purchase || 0,
      max_discount: coupon.max_discount || null,
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      usage_limit: coupon.usage_limit || null,
      user_limit: coupon.user_limit || 1,
      applicable_to: coupon.applicable_to || [],
      is_active: coupon.is_active
    });
    setEditingId(coupon.id);
    setShowForm(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadCoupons();
    } catch (error) {
      alert('Failed to delete coupon');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_purchase: 0,
      max_discount: null,
      valid_until: '',
      usage_limit: null,
      user_limit: 1,
      applicable_to: [],
      is_active: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  const toggleApplicableTo = (type) => {
    setFormData(prev => ({
      ...prev,
      applicable_to: prev.applicable_to.includes(type)
        ? prev.applicable_to.filter(t => t !== type)
        : [...prev.applicable_to, type]
    }));
  };

  return (
    <div className="space-y-6" data-testid="manage-coupons">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Tag className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Coupons
          </h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="cosmic-btn flex items-center gap-2"
        >
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span>{showForm ? 'Cancel' : 'Add Coupon'}</span>
        </button>
      </div>

      {showForm && (
        <div className="glass-card p-6">
          <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>
            {editingId ? 'Edit Coupon' : 'Create New Coupon'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Coupon Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="cosmic-input w-full"
                  placeholder="e.g., WELCOME20"
                  required
                  disabled={!!editingId}
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Description *
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="e.g., 20% off for new users"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Discount Type
                </label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className="cosmic-input w-full"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Discount Value *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                    className="cosmic-input w-full pl-10"
                    min="0"
                    step="0.01"
                    required
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: currentColors.textMuted }}>
                    {formData.discount_type === 'percentage' ? <Percent className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Max Discount (for %)
                </label>
                <input
                  type="number"
                  value={formData.max_discount || ''}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value ? parseFloat(e.target.value) : null })}
                  className="cosmic-input w-full"
                  placeholder="No limit"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Min Purchase ($)
                </label>
                <input
                  type="number"
                  value={formData.min_purchase}
                  onChange={(e) => setFormData({ ...formData, min_purchase: parseFloat(e.target.value) || 0 })}
                  className="cosmic-input w-full"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Valid Until
                </label>
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className="cosmic-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Usage Limit
                </label>
                <input
                  type="number"
                  value={formData.usage_limit || ''}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value ? parseInt(e.target.value) : null })}
                  className="cosmic-input w-full"
                  placeholder="Unlimited"
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>
                Applicable To (leave empty for all)
              </label>
              <div className="flex gap-2">
                {['meditation', 'article', 'subscription'].map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleApplicableTo(type)}
                    className="px-4 py-2 rounded-lg text-sm transition-all"
                    style={{
                      backgroundColor: formData.applicable_to.includes(type) 
                        ? `${currentColors.primary}20` 
                        : `${currentColors.textMuted}10`,
                      color: formData.applicable_to.includes(type) 
                        ? currentColors.primary 
                        : currentColors.textMuted,
                      border: `1px solid ${formData.applicable_to.includes(type) ? currentColors.primary : 'transparent'}`
                    }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}s
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                className={`w-12 h-6 rounded-full transition-all relative`}
                style={{ backgroundColor: formData.is_active ? currentColors.primary : currentColors.textMuted }}
              >
                <div 
                  className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                    formData.is_active ? 'right-0.5' : 'left-0.5'
                  }`}
                />
              </button>
              <span style={{ color: currentColors.text }}>
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <button type="submit" className="cosmic-btn w-full flex items-center justify-center gap-2">
              <Save className="w-5 h-5" />
              <span>{editingId ? 'Update Coupon' : 'Create Coupon'}</span>
            </button>
          </form>
        </div>
      )}

      {/* Coupons Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Code</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Discount</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Usage</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Status</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => (
                <tr key={coupon.id} style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                  <td className="py-3 px-4">
                    <div>
                      <code 
                        className="px-2 py-1 rounded text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                      >
                        {coupon.code}
                      </code>
                      <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
                        {coupon.description}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span style={{ color: currentColors.text }}>
                      {coupon.discount_type === 'percentage' 
                        ? `${coupon.discount_value}%` 
                        : `$${coupon.discount_value}`
                      }
                    </span>
                    {coupon.min_purchase > 0 && (
                      <p className="text-xs" style={{ color: currentColors.textMuted }}>
                        Min: ${coupon.min_purchase}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span style={{ color: currentColors.text }}>
                      {coupon.usage_count || 0}
                      {coupon.usage_limit && ` / ${coupon.usage_limit}`}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="px-3 py-1 text-xs rounded-full"
                      style={{ 
                        backgroundColor: coupon.is_active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: coupon.is_active ? '#22c55e' : '#ef4444'
                      }}
                    >
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(coupon)}
                        className="p-2 rounded-lg transition-all"
                        style={{ color: currentColors.accent }}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(coupon.id)}
                        className="p-2 rounded-lg transition-all text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-8 text-center" style={{ color: currentColors.textMuted }}>
                    No coupons created yet. Create your first coupon to offer discounts!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
